/**
 * REGROUP a cohort pairing after the fact (GROUP-ORIENTATION-PLAN
 * decision 2026-09-18: groups are normally decided at assign time on the
 * Orientation Assignment page; this drawer stays as the after-the-fact
 * path — a late joiner back-filled as an individual, a group split).
 * Pick members from the pairing's ASSIGNED members, name the group, pick
 * the leader (defaults to the current user; any site user — a senior
 * worker can lead, locked decision 9) and the mode.
 *
 * The leader must not be a member (decision 9); the picker enforces it
 * by dropping the leader from the member selection on save.
 */
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { useMemo, useState } from 'react';
import { firstReason } from '@/lib/disabledReason';
import { GatedButton } from '../GatedButton';
import { MemberPicker } from './MemberPicker';

export function CreateGroupDrawer({
  pairingLabel,
  /** Emails ASSIGNED under this pairing (kind === 'assigned'), including
   *  blocked ones — those render disabled with the reason on hover. */
  assignedEmails,
  blockedReasons,
  /** Site roster for the leader picker. */
  siteEmails,
  currentUserEmail,
  busy,
  onCancel,
  onCreate,
}: {
  pairingLabel: string;
  assignedEmails: string[];
  /** email -> why this member can't be grouped (hover text on the chip). */
  blockedReasons: ReadonlyMap<string, string>;
  siteEmails: string[];
  currentUserEmail: string;
  busy: boolean;
  onCancel: () => void;
  onCreate: (args: {
    name: string;
    memberEmails: string[];
    leaderEmail: string;
    modeTag: 'Managed' | 'Auto';
    allowLateJoin: boolean;
    autoStallSeconds: number;
  }) => void;
}) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(assignedEmails.filter((e) => !blockedReasons.has(e))),
  );
  const [leaderEmail, setLeaderEmail] = useState(currentUserEmail);
  const [modeTag, setModeTag] = useState<'Managed' | 'Auto'>('Managed');
  // Phase 10: late_join_gate refuses a NotStarted member when off (a
  // rejoin is never refused); the stall countdown advances a stuck Auto
  // step once the first finished member has waited it out (0 = never).
  const [allowLateJoin, setAllowLateJoin] = useState(true);
  const [autoStallSeconds, setAutoStallSeconds] = useState(30);

  const memberEmails = useMemo(
    () => Array.from(selected).filter((e) => e !== leaderEmail),
    [selected, leaderEmail],
  );
  const createReason = firstReason(
    [name.trim() === '', 'Name the group first'],
    [memberEmails.length === 0, 'Pick at least one member (the leader does not count)'],
    [leaderEmail === '', 'Pick a leader'],
  );

  return (
    <>
      <div
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 40 }}
      />
      <aside
        role="dialog"
        aria-label="Regroup"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          maxWidth: '92vw',
          background: 'var(--panel)',
          color: 'var(--text)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.4)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>Regroup</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{pairingLabel}</div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onCancel}
            aria-label="Close"
            style={{ width: 22, height: 22 }}
          >
            <Icon of={Icons.action.remove} size={14} />
          </button>
        </header>

        <div style={{ padding: 12, display: 'grid', gap: 10, overflowY: 'auto', flex: 1, alignContent: 'start' }}>
          <input
            type="text"
            className="inp"
            placeholder='Group name, e.g. "Monday AM"'
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Group name"
          />

          <label style={{ display: 'grid', gap: 3, fontSize: 12 }}>
            <span style={{ color: 'var(--muted)', fontSize: 11 }}>
              {modeTag === 'Auto'
                ? 'Leader — can override the run, is not graded, does not have to join. Any site user.'
                : 'Leader — drives the run, is not graded, can be any site user'}
            </span>
            <select
              className="inp"
              value={leaderEmail}
              onChange={(e) => setLeaderEmail(e.target.value)}
              aria-label="Leader"
            >
              {[currentUserEmail, ...siteEmails.filter((e) => e !== currentUserEmail)].map(
                (e) => (
                  <option key={e} value={e}>
                    {e}
                    {e === currentUserEmail ? ' (you)' : ''}
                  </option>
                ),
              )}
            </select>
          </label>

          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Mode</span>
            {(['Managed', 'Auto'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={'chip-filter ' + (modeTag === m ? 'on' : '')}
                onClick={() => setModeTag(m)}
                aria-pressed={modeTag === m}
                title={
                  m === 'Managed'
                    ? 'The leader starts, continues and stops the run.'
                    : 'Starts when everyone is ready; advances when every connected member finishes the step, or after the stall countdown. The leader can still override, and does not have to be present.'
                }
              >
                {m}
              </button>
            ))}
          </div>

          <label
            style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}
            title="Off: a member who never started cannot enter once the run is Running (their picker row reads late join closed). A dropped member can always rejoin."
          >
            <input
              type="checkbox"
              checked={allowLateJoin}
              onChange={(e) => setAllowLateJoin(e.target.checked)}
              style={{ width: 14, height: 14 }}
            />
            Allow late joiners
          </label>

          {modeTag === 'Auto' ? (
            <label
              style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}
              title="Once the first member finishes a stop, the run moves on after this many seconds even if others are still answering (their open checks expire). 0 = wait forever."
            >
              Stall seconds
              <input
                type="number"
                className="inp"
                min={0}
                max={3600}
                value={autoStallSeconds}
                onChange={(e) => {
                  const v = Number.parseInt(e.target.value, 10);
                  setAutoStallSeconds(Number.isFinite(v) && v >= 0 ? v : 0);
                }}
                aria-label="Stall seconds (0 = never)"
                style={{ width: 80, fontSize: 12, padding: '3px 6px' }}
              />
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>0 = never</span>
            </label>
          ) : null}

          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            Members — only this orientation&apos;s assigned, not-started members
            can be grouped{leaderEmail && selected.has(leaderEmail) ? ' (the leader is excluded automatically)' : ''}:
          </div>
          <MemberPicker
            emails={assignedEmails}
            disabledReasons={blockedReasons}
            selected={selected}
            onToggle={(email) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(email)) next.delete(email);
                else next.add(email);
                return next;
              })
            }
          />
        </div>

        <footer
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <GatedButton
            className="btn primary"
            busy={busy}
            reason={createReason}
            onClick={() =>
              onCreate({
                name: name.trim(),
                memberEmails,
                leaderEmail,
                modeTag,
                allowLateJoin,
                autoStallSeconds,
              })
            }
          >
            {busy
              ? 'Creating…'
              : `Create with ${memberEmails.length} member${memberEmails.length === 1 ? '' : 's'}`}
          </GatedButton>
        </footer>
      </aside>
    </>
  );
}
